import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  Input,
  Label,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterLitigationDatePlanDate,
  useDeleteMatterLitigationDatePlanDate,
  useUpdateMatterLitigationDatePlanDate,
} from "@/lib/data/react-query/matters/matter-litigation-date-plan-date";
import { MatterLitigationDatePlanDateSchema } from "@/lib/data/schemas/matters/MatterSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type MatterLitigationDatePlanDate,
  type MatterLitigationDateType,
} from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import pluralize from "pluralize";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditMatterLitigationDatePlanDateModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  planId: string;
  editing: boolean;
  activeDate?: MatterLitigationDatePlanDate;
  isReadOnly?: boolean;
  litigationDateTypes: MatterLitigationDateType[];
  isLitigationDateTypesLoading?: boolean;
  planDates?: MatterLitigationDatePlanDate[];
}

const formSchema = MatterLitigationDatePlanDateSchema.pick({
  deadline: true,
  triggerId: true,
  typeId: true,
}).extend({
  deadline: z
    .string()
    .trim()
    .min(1, { message: "Deadline is required" })
    .refine((value) => !Number.isNaN(Number(value)), {
      message: "Deadline must be a number",
    })
    .refine(
      (value) => {
        const numberValue = Number(value);
        return numberValue >= 0 && Number.isInteger(numberValue);
      },
      {
        message: "Deadline must be a whole number of days",
      },
    ),
  triggerId: z.string().min(1, { message: "Trigger is required" }),
  typeId: z.string().min(1, { message: "Litigation event is required" }),
  deadlineDirection: z.enum(["before", "after"]),
});

type FormValues = z.infer<typeof formSchema>;

const CREATE_FORM_ID = "create-matter-litigation-plan-date-form";
const EDIT_FORM_ID = "edit-matter-litigation-plan-date-form";
const PLAN_DATE_SELECT_CONTENT_CLASS_NAME = "max-w-[min(90vw,32rem)]";
const PLAN_DATE_SELECT_ITEM_CLASS_NAME = "max-w-[min(90vw,32rem)]";

const wouldCreateCircularReference = (
  planDates: MatterLitigationDatePlanDate[],
  triggerId: string,
  dateTypeId: string,
  excludePlanDateId?: string,
) => {
  if (!triggerId || !dateTypeId) return false;
  if (triggerId === dateTypeId) return true;

  const adjacencyMap = planDates.reduce<Map<string, Set<string>>>(
    (map, planDate) => {
      if (
        planDate.id === excludePlanDateId ||
        !planDate.triggerId ||
        !planDate.typeId
      ) {
        return map;
      }

      const trigger = planDate.triggerId.toString();
      const target = planDate.typeId.toString();

      if (!map.has(trigger)) {
        map.set(trigger, new Set());
      }

      map.get(trigger)?.add(target);
      return map;
    },
    new Map(),
  );

  const visited = new Set<string>();
  const stack = [dateTypeId];

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;
    if (current === triggerId) return true;
    if (visited.has(current)) continue;

    visited.add(current);

    const neighbors = adjacencyMap.get(current);
    if (neighbors) {
      neighbors.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          stack.push(neighbor);
        }
      });
    }
  }

  return false;
};

export const AddOrEditMatterLitigationDatePlanDateModal = ({
  open,
  setOpen,
  planId,
  editing,
  activeDate,
  isReadOnly,
  litigationDateTypes,
  isLitigationDateTypesLoading,
  planDates = [],
}: AddOrEditMatterLitigationDatePlanDateModalProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);

  const defaultFormValues = useMemo<FormValues>(
    () => ({
      deadline: "",
      triggerId: "",
      typeId: "",
      deadlineDirection: "after",
    }),
    [],
  );

  const formValues = useMemo<FormValues>(() => {
    if (!editing || !activeDate) {
      return defaultFormValues;
    }

    const deadline = activeDate.deadline;

    return {
      deadline: Math.abs(deadline).toString(),
      triggerId: activeDate.triggerId,
      typeId: activeDate.typeId,
      deadlineDirection: deadline < 0 ? "before" : "after",
    };
  }, [activeDate, defaultFormValues, editing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: formValues,
    mode: "onSubmit",
  });

  const createMatterLitigationDatePlanDate =
    useCreateMatterLitigationDatePlanDate();
  const updateMatterLitigationDatePlanDate =
    useUpdateMatterLitigationDatePlanDate();
  const deleteMatterLitigationDatePlanDate =
    useDeleteMatterLitigationDatePlanDate();

  const isFormLoading =
    createMatterLitigationDatePlanDate.isPending ||
    updateMatterLitigationDatePlanDate.isPending ||
    deleteMatterLitigationDatePlanDate.isPending;

  const formId = editing ? EDIT_FORM_ID : CREATE_FORM_ID;
  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length > 0;
  const isFormDirty = form.formState.isDirty || hasDirtyFields;

  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDatePlanDate,
    ACTION_PERMISSION_VALUES.delete,
  );

  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDatePlanDate,
    ACTION_PERMISSION_VALUES.update,
  );

  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDatePlanDate,
    ACTION_PERMISSION_VALUES.create,
  );

  const dateTypeLookup = useMemo(
    () =>
      new Map(
        litigationDateTypes.map((dateType) => [dateType.id, dateType.label]),
      ),
    [litigationDateTypes],
  );

  const watchedEventId = form.watch("typeId");
  const watchedTriggerId = form.watch("triggerId");
  const watchedDeadline = form.watch("deadline");
  const watchedDirection = form.watch("deadlineDirection");

  const previewText = useMemo(() => {
    const eventLabel = dateTypeLookup.get(watchedEventId);
    const triggerLabel = dateTypeLookup.get(watchedTriggerId);
    const normalizedDeadline = watchedDeadline.trim();

    if (!eventLabel || !triggerLabel || normalizedDeadline.length === 0) {
      return null;
    }

    const deadlineText = pluralize("day", Number(normalizedDeadline), true);

    return `${eventLabel} — ${deadlineText} ${watchedDirection} ${triggerLabel}`;
  }, [
    dateTypeLookup,
    watchedDeadline,
    watchedDirection,
    watchedEventId,
    watchedTriggerId,
  ]);

  useEffect(() => {
    if (open) {
      form.reset(formValues);
      return;
    }

    form.reset(formValues);
    setIsDeleting(false);
  }, [form, formValues, open]);

  const handleSubmit = async (values: FormValues) => {
    if (!planId) return;

    const triggerId = values.triggerId;
    const dateTypeId = values.typeId;
    const isEditingExistingPair =
      editing &&
      triggerId === activeDate?.triggerId &&
      dateTypeId === activeDate.typeId;

    if (
      !isEditingExistingPair &&
      wouldCreateCircularReference(
        planDates,
        triggerId,
        dateTypeId,
        activeDate?.id,
      )
    ) {
      const message =
        "Selecting this trigger and event would create a circular reference.";
      form.setError("triggerId", {
        type: "manual",
        message,
      });
      form.setError("typeId", {
        type: "manual",
        message,
      });
      toast.error(message);
      return;
    }

    const rawDeadline = Number(values.deadline.trim());
    const signedDeadline =
      values.deadlineDirection === "before"
        ? -Math.abs(rawDeadline)
        : rawDeadline;

    if (editing) {
      if (!activeDate) return;

      if (!hasUpdatePermission) {
        toast.error(
          "You do not have permission to update this litigation plan date",
        );
        return;
      }

      await updateMatterLitigationDatePlanDate.mutateAsync({
        id: activeDate.id,
        planId,
        deadline: signedDeadline,
        triggerId,
        typeId: dateTypeId,
        dateModified: activeDate.dateModified,
      });
    } else {
      if (!hasCreatePermission) {
        toast.error(
          "You do not have permission to create litigation plan dates",
        );
        return;
      }

      await createMatterLitigationDatePlanDate.mutateAsync({
        planId,
        deadline: signedDeadline,
        triggerId,
        typeId: dateTypeId,
      });
    }

    setOpen(false);
  };

  const handleDelete = async () => {
    if (!activeDate) return;

    setIsDeleting(false);
    setOpen(false);

    await deleteMatterLitigationDatePlanDate.mutateAsync({
      id: activeDate.id,
      planId,
    });
  };

  const isSubmitDisabled =
    isFormLoading || (editing ? !hasUpdatePermission : !hasCreatePermission);

  const eventFieldError = form.formState.errors.typeId;
  const deadlineFieldError = form.formState.errors.deadline;
  const triggerFieldError = form.formState.errors.triggerId;

  const renderDateTypeOptions = () => (
    <>
      {litigationDateTypes.map((item) => (
        <SelectItem
          key={item.id}
          value={item.id}
          className={PLAN_DATE_SELECT_ITEM_CLASS_NAME}
        >
          <span className="line-clamp-2 break-words">{item.label}</span>
        </SelectItem>
      ))}
      {isLitigationDateTypesLoading ? (
        <SelectItem value="loading" disabled>
          Loading...
        </SelectItem>
      ) : null}
    </>
  );

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={editing ? "Edit plan date" : "Add plan date"}
          size="2xl"
          loading={isFormLoading}
          isDirty={isFormDirty}
          footer={
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
              {!isReadOnly && editing && hasDeletePermission ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="sm:mr-auto"
                  onClick={() => {
                    setIsDeleting(true);
                  }}
                >
                  Delete
                </Button>
              ) : (
                <div className="hidden sm:block sm:flex-1" />
              )}
              <div className="flex justify-end gap-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={isFormDirty}
                />
                {!isReadOnly ? (
                  <Button
                    type="submit"
                    form={formId}
                    disabled={isSubmitDisabled || !isFormDirty}
                  >
                    {isFormLoading ? <LoadingSpinner /> : "Save"}
                  </Button>
                ) : null}
              </div>
            </div>
          }
        >
          <form
            id={formId}
            className="space-y-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <fieldset disabled={isReadOnly} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="matter-litigation-date-type">
                  Litigation event
                </Label>
                <Select
                  value={watchedEventId}
                  onValueChange={(value) => {
                    form.clearErrors(["typeId", "triggerId"]);
                    form.setValue("typeId", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  disabled={isReadOnly}
                >
                  <SelectTrigger
                    id="matter-litigation-date-type"
                    className="w-full sm:max-w-[340px]"
                  >
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent
                    className={PLAN_DATE_SELECT_CONTENT_CLASS_NAME}
                  >
                    {renderDateTypeOptions()}
                  </SelectContent>
                </Select>
                {eventFieldError?.message ? (
                  <Text variant="caption" color="destructive">
                    {eventFieldError.message}
                  </Text>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label>Deadline rule</Label>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="w-full sm:max-w-20">
                    <Input
                      inputMode="numeric"
                      placeholder="0"
                      value={watchedDeadline}
                      onChange={(event) => {
                        form.setValue("deadline", event.target.value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  </div>
                  <Text variant="subHeading" className="shrink-0">
                    days
                  </Text>
                  <Select
                    value={watchedDirection}
                    onValueChange={(value) => {
                      form.setValue(
                        "deadlineDirection",
                        value as "before" | "after",
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        },
                      );
                    }}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="w-full sm:max-w-[340px]">
                      <SelectValue placeholder="Select timing" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="after">after</SelectItem>
                      <SelectItem value="before">before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {deadlineFieldError?.message ? (
                  <Text variant="caption" color="destructive">
                    {deadlineFieldError.message}
                  </Text>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="matter-litigation-date-trigger">Trigger</Label>
                <Select
                  value={watchedTriggerId}
                  onValueChange={(value) => {
                    form.clearErrors(["triggerId", "typeId"]);
                    form.setValue("triggerId", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  disabled={isReadOnly}
                >
                  <SelectTrigger
                    id="matter-litigation-date-trigger"
                    className="w-full sm:max-w-[340px]"
                  >
                    <SelectValue placeholder="Select trigger" />
                  </SelectTrigger>
                  <SelectContent
                    className={PLAN_DATE_SELECT_CONTENT_CLASS_NAME}
                  >
                    {renderDateTypeOptions()}
                  </SelectContent>
                </Select>
                <Text variant="subHeading">
                  The event this deadline is calculated from.
                </Text>
                {triggerFieldError?.message ? (
                  <Text variant="caption" color="destructive">
                    {triggerFieldError.message}
                  </Text>
                ) : null}
              </div>

              {previewText ? (
                <div className="bg-muted/40 border-border/70 space-y-2 rounded-xl border px-4 py-3">
                  <Text
                    variant="caption"
                    className="font-semibold tracking-[0.12em] uppercase"
                  >
                    Preview
                  </Text>
                  <Text variant="body">{previewText}</Text>
                </div>
              ) : null}
            </fieldset>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this plan date?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
